import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import type { SpaceMemberRole, SpaceMemberStatus } from "@/lib/supabase";
import type { TribeMembership } from "@/types/tribe";
import { useAuth } from "./useAuth";

const roleOrder: Record<SpaceMemberRole, number> = {
  owner: 0,
  member: 1,
  contributor: 2,
  child: 3,
};

async function resolveSpaces(userId: string): Promise<TribeMembership[]> {
  const { data, error } = await supabase
    .from("space_members")
    .select(
      "id, role, status, space_id, spaces:spaces!space_id(id, name, owner_id, owner:profiles!owner_id(nickname))",
    )
    .eq("user_id", userId)
    .eq("status", "active");

  if (error || !data) return [];

  const rows: TribeMembership[] = data.map((d) => {
    const spaceRow = d.spaces as unknown as
      | {
          id: string;
          name: string;
          owner_id: string;
          owner: { nickname: string } | null;
        }
      | null;
    const role = d.role as SpaceMemberRole;
    return {
      id: d.id as string,
      role,
      status: d.status as SpaceMemberStatus,
      spaceId: spaceRow?.id ?? (d.space_id as string),
      spaceName: spaceRow?.name ?? "Family",
      ownerNickname: spaceRow?.owner?.nickname ?? "Someone",
      ownerId: spaceRow?.owner_id ?? "",
      avatarUrl: null,
      isOwnedByMe: role === "owner" || spaceRow?.owner_id === userId,
    };
  });

  rows.sort((a, b) => {
    if (a.isOwnedByMe !== b.isOwnedByMe) return a.isOwnedByMe ? -1 : 1;
    return roleOrder[a.role] - roleOrder[b.role];
  });

  return rows;
}

export function useSpaces(): {
  data: TribeMembership[] | undefined;
  spaces: TribeMembership[];
  isLoading: boolean;
  isError: boolean;
  error: Error | null;
  refetch: () => Promise<unknown>;
} {
  const { user } = useAuth();
  const userId = user?.id;

  const query = useQuery({
    queryKey: ["spaces", userId],
    enabled: !!userId,
    queryFn: () => resolveSpaces(userId!),
  });

  return {
    data: query.data,
    spaces: query.data ?? [],
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error as Error | null,
    refetch: query.refetch,
  };
}
